// Google Analytics 4 setup for comprehensive tracking
// Get your GA Measurement ID from Google Analytics > Admin > Data Streams

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Your Google Analytics 4 Measurement ID
export const GA_MEASUREMENT_ID = 'G-CWBLKJY8G2';

// Check if user has consented to analytics cookies
export const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const consent = localStorage.getItem('cookie-consent');
  if (!consent) return false;
  
  try {
    const preferences = JSON.parse(consent);
    return preferences.analytics === true;
  } catch {
    return false;
  }
};

// Initialize Google Analytics (only if user has consented)
export const initGA = () => {
  if (typeof window !== 'undefined' && GA_MEASUREMENT_ID && hasAnalyticsConsent()) {
    // Load Google Analytics script dynamically after consent
    if (!window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
    }
    
    // Track initial page view with enhanced parameters
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }
};

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
      page_title: title || document.title,
    });
  }
};

// Track events
export const trackEvent = (action: string, category: string, label?: string, value?: number, customParams?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
    const eventParams: any = {
      event_category: category,
      event_label: label,
      value: value,
      ...customParams
    };
    
    window.gtag('event', action, eventParams);
  }
};

// Track scroll depth
export const trackScrollDepth = () => {
  if (typeof window !== 'undefined' && hasAnalyticsConsent()) {
    let maxScroll = 0;
    const trackScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
        maxScroll = scrollPercent;
        trackEvent('scroll', 'engagement', `${scrollPercent}%`);
      }
    };
    window.addEventListener('scroll', trackScroll);
  }
};

// Track form submissions
export const trackFormSubmission = (formName: string) => {
  trackEvent('form_submit', 'engagement', formName);
};

// Track button clicks
export const trackButtonClick = (buttonName: string) => {
  trackEvent('click', 'engagement', buttonName);
};

// Track external link clicks
export const trackExternalLink = (url: string) => {
  trackEvent('click', 'outbound', url);
};

// Track portfolio project views
export const trackPortfolioView = (projectName: string, projectType: string) => {
  trackEvent('view_item', 'portfolio', `${projectName} - ${projectType}`);
};

// Track portfolio project clicks (live site or GitHub)
export const trackPortfolioClick = (projectName: string, linkType: 'live' | 'github', url: string) => {
  trackEvent('click', 'portfolio', `${projectName} - ${linkType}`, undefined, {
    link_url: url,
    project_name: projectName,
    link_type: linkType
  });
};

// Track contact form interactions
export const trackContactFormStart = () => {
  trackEvent('form_start', 'contact', 'contact_form');
};

// Track contact form completion
export const trackContactFormComplete = (projectType: string) => {
  trackEvent('form_submit', 'contact', 'contact_form', undefined, {
    project_type: projectType
  });
};

// Track social media clicks
export const trackSocialClick = (platform: string, url: string) => {
  trackEvent('click', 'social', platform, undefined, {
    social_platform: platform,
    link_url: url
  });
};

// Track language switching
export const trackLanguageSwitch = (fromLang: string, toLang: string) => {
  trackEvent('language_switch', 'engagement', `${fromLang} to ${toLang}`, undefined, {
    from_language: fromLang,
    to_language: toLang
  });
};

// Enhanced event tracking with custom parameters
export const trackCustomEvent = (action: string, category: string, label?: string, value?: number, customParams?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
    const eventParams: any = {
      event_category: category,
      event_label: label,
      value: value,
      ...customParams
    };
    
    window.gtag('event', action, eventParams);
  }
};

// Track user engagement metrics
export const trackEngagement = (metric: string, value: number) => {
  trackCustomEvent('engagement', 'user_behavior', metric, value, {
    engagement_metric: metric
  });
};
